import { request } from '@/utils/requestHelpers';
import { CreateBatchRelativeInsightsParams } from './batchRelativeInsightsInterfaces';
import {
  CreateVehicleSmartInsightParams,
  LLMRequestsInterface,
  LLMRequestsPersonInterface,
  LLMRequestsSmartSearchV1,
  LLMRequestsStaypalInterface,
  type LLMRequestsUtilsInterface,
} from './interfaces';
import { CreateObituaryParserParams } from './obituaryParserInterfaces';
import { CreatePersonReportSummaryParams } from './personReportSummaryInterfaces';
import {
  CreatePropertySmartInsightsParams,
  LLMRequestsPropertyInterface,
} from './propertySmartInsightsInterfaces';
import { CreateRelativeInsightsParams } from './relativeInsightsInterfaces';
import { CreateSmartSearchV1Params } from './smartSearchV1Interfaces';
import { CreateStaypalParams } from './staypalInterfaces';
import { type CreateTimezoneAndCoordinatesParams } from './timezoneAndCoordinatesInterfaces';

const llmUrl = '/ms/insight/';

const createInit = (
  token: string,
  params:
    | CreateVehicleSmartInsightParams
    | CreatePropertySmartInsightsParams
    | CreatePersonReportSummaryParams
    | CreateRelativeInsightsParams
    | CreateBatchRelativeInsightsParams
    | CreateSmartSearchV1Params
    | CreateObituaryParserParams
    | CreateTimezoneAndCoordinatesParams
) => {
  return {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    method: 'POST',
    body: JSON.stringify(params),
  };
};

const createVehicleSmartInsight = async (
  token: string,
  params: CreateVehicleSmartInsightParams
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}vehicleSmartInsights`, init);
};

export const createPropertySmartInsight = async (
  token: string,
  params: CreatePropertySmartInsightsParams
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}propertySmartInsights`, init);
};

const createPersonReportSummary = async (
  token: string,
  params: CreatePersonReportSummaryParams,
  errorHandler?: (error: Response) => Promise<unknown | void>
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}personReportSummary`, init, '', errorHandler);
};

const createRelativeInsight = async (
  token: string,
  params: CreateRelativeInsightsParams
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}relativeInsights`, init);
};

const createBatchRelativeInsight = async (
  token: string,
  params: CreateBatchRelativeInsightsParams
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}batchRelativeInsights`, init);
};

const createSmartSearchV1 = async (
  token: string,
  params: CreateSmartSearchV1Params
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}smartSearchV1`, init);
};

const createPersonSmartSearchV1 = async (
  token: string,
  params: CreateSmartSearchV1Params
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}personSmartSearch`, init);
};

const createVehicleSmartSearchV1 = async (
  token: string,
  params: CreateSmartSearchV1Params
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}vehicleSmartSearch`, init);
};

const createObituaryParser = async (
  token: string,
  params: CreateObituaryParserParams
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}obituaryParser`, init);
};

const createTimezoneAndCoordinates = async (
  token: string,
  params: CreateTimezoneAndCoordinatesParams
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}timezoneAndCoordinates`, init);
};

const createStaypalRequest = async (
  token: string,
  params: CreateStaypalParams
) => {
  const init = createInit(token, params);
  return await request(`${llmUrl}staypal`, init);
};

export const llm: LLMRequestsInterface = {
  createVehicleSmartInsight,
};

export const llmProperty: LLMRequestsPropertyInterface = {
  createPropertySmartInsight,
};

export const llmPerson: LLMRequestsPersonInterface = {
  createPersonReportSummary,
  createRelativeInsight,
  createBatchRelativeInsight,
  createObituaryParser,
};

export const llmSmartSearchV1: LLMRequestsSmartSearchV1 = {
  createSmartSearchV1,
  createPersonSmartSearchV1,
  createVehicleSmartSearchV1,
};

export const llmUtils: LLMRequestsUtilsInterface = {
  createTimezoneAndCoordinates,
};

export const llmStaypal: LLMRequestsStaypalInterface = {
  createStaypalRequest,
};
